import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import React, { useState } from 'react'

import {
  apiEndpoints,
  fetchInterestedProgramsOptions,
  fetchResolutionConclusionsOptions,
  fetchTicketTypeOptions
} from 'api'
import { TextField, Select } from 'app/components/generic/FormElements'
import { AutoComplete, DatePicker } from 'app/components/generic/FormFields'
import { FOLLOW_UP_DATE_MESSAGE, TICKET_STATUS_OPTIONS } from 'app/constants'
import { validateDate, validateNotEmpty } from 'app/validators'

import * as styles from './styles'

import { get } from '../../../../../request'

const EditTicketStatusForm = (props) => {
  const { formData } = props
  const { errors, values, setFieldValue } = useFormikContext()

  const [statusOptions, setStatusOptions] = useState([])
  const [resolutionConclusionOptions, setResolutionConclusionOptions] =
    useState([])
  const [ticketTypeOptions, setTicketTypeOptions] = useState([])
  const [interestedProgramsOptions, setInterestedProgramsOptions] = useState([])
  const conversionIntentOptions = ['High', 'Medium', 'Low']

  const monthOptions = React.useMemo(() => {
    const options = []
    const start = dayjs().startOf('month')
    for (let i = 0; i <= 12; i++) {
      const d = start.add(i, 'month')
      options.push({
        uuid: d.endOf('month').format('YYYY-MM-DD'),
        name: d.format('MMM YYYY')
      })
    }
    return options
  }, [])

  const fetchInterestedPrograms = async () => {
    try {
      const response = await fetchInterestedProgramsOptions()
      if (response) {
        const programsArray = response?.results?.[0]?.value

        const programsMapping =
          programsArray?.length > 0
            ? programsArray.map((program) => ({
                uuid: program,
                name: program
              }))
            : []

        setInterestedProgramsOptions(programsMapping)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error.toString())
    }
  }

  //UseEffect for fetching deduction reasons array
  React.useEffect(() => {
    ;(async () => {
      const statusConfig = await get(
        `${apiEndpoints.configApp}?key=CRM_TICKET_STATUSES`
      )
      setStatusOptions(statusConfig?.data?.results?.[0].value)
      const ticketTypeConfig = await fetchTicketTypeOptions()
      setTicketTypeOptions(ticketTypeConfig?.results?.[0].value)
      const resolutionConclusionConfig =
        await fetchResolutionConclusionsOptions()
      setResolutionConclusionOptions(
        resolutionConclusionConfig?.results?.[0].value
      )
      fetchInterestedPrograms()
    })()
  }, [])

  React.useEffect(() => {
    if (formData?.status) {
      setFieldValue('status', formData?.status)
    }
    if (formData?.resolution_summary) {
      setFieldValue('resolution_summary', formData?.resolution_summary)
    }
    if (formData?.resolution_conclusion) {
      setFieldValue('resolution_conclusion', formData?.resolution_conclusion)
    }
    if (formData?.follow_up_date) {
      setFieldValue('follow_up_date', formData?.follow_up_date)
    }
    if (formData?.conversion_intent) {
      setFieldValue('conversion_intent', formData?.conversion_intent)
    }
    if (formData?.interested_programs) {
      setFieldValue('interested_programs', formData?.interested_programs)
    }
    if (formData?.tentative_joining_date) {
      setFieldValue('tentative_joining_date', formData?.tentative_joining_date)
    }
    if (formData?.intent_reason) {
      setFieldValue('intent_reason', formData?.intent_reason)
    }
  }, [
    formData?.status,
    formData?.resolution_summary,
    formData?.resolution_conclusion,
    formData?.follow_up_date
  ])

  React.useEffect(() => {
    if (
      values?.status === TICKET_STATUS_OPTIONS.RESOLVED &&
      formData.follow_up_date !== dayjs().format('YYYY-MM-DD') &&
      dayjs(formData.follow_up_date).isAfter(dayjs()) &&
      formData?.status !== TICKET_STATUS_OPTIONS.RESOLVED
    )
      setFieldValue('follow_up_date', dayjs().format('YYYY-MM-DD'))
  }, [values?.status])

  const validateResolutionSummary = (value) => {
    if (values?.status === TICKET_STATUS_OPTIONS.RESOLVED && isEmpty(value)) {
      return 'Summary is required when status is Resolved'
    }
  }

  const onDateChangeHandler = (e) => {
    setFieldValue('follow_up_date', e.target.value)
  }

  return (
    <>
      <FormControl variant="outlined" required>
        <InputLabel>{'Status'}</InputLabel>
        <Select
          name={'status'}
          defaultValue={formData?.status || ''}
          label={'Status'}
          validate={validateNotEmpty({ isRequired: true })}
        >
          {statusOptions?.map((key, index) => {
            return (
              <MenuItem key={statusOptions[index]} value={statusOptions[index]}>
                {statusOptions[index]}
              </MenuItem>
            )
          })}
        </Select>
        {!isEmpty(errors.status) && (
          <FormHelperText>{errors.status}</FormHelperText>
        )}
      </FormControl>
      {values?.status !== TICKET_STATUS_OPTIONS.RESOLVED ? (
        <>
          <FormControl
            variant="outlined"
            {...(!isEmpty(errors.follow_up_date) ? { error: true } : null)}
            required
          >
            <DatePicker
              inputProps={{ min: dayjs().format('YYYY-MM-DD') }}
              name={'follow_up_date'}
              label={'Follow Up Date'}
              value={values?.follow_up_date}
              onChange={onDateChangeHandler}
              variant="outlined"
              required
              validate={validateDate}
            />
          </FormControl>
          {values?.status !== TICKET_STATUS_OPTIONS.RESOLVED &&
          ticketTypeOptions?.filter(
            (item) => item.key === formData?.ticket_type
          )?.[0]?.enable_intent_section ? (
            <FormControl variant="outlined" required>
              <InputLabel>{'Intent'}</InputLabel>
              <Select
                name={'conversion_intent'}
                defaultValue={formData?.conversion_intent || ''}
                label={'Intent'}
                validate={validateNotEmpty({ isRequired: true })}
              >
                {conversionIntentOptions?.map((key, index) => {
                  return (
                    <MenuItem
                      key={conversionIntentOptions[index]}
                      value={conversionIntentOptions[index]}
                    >
                      {conversionIntentOptions[index]}
                    </MenuItem>
                  )
                })}
              </Select>
              {!isEmpty(errors.conversion_intent) && (
                <FormHelperText>{errors.conversion_intent}</FormHelperText>
              )}
            </FormControl>
          ) : null}
          {values?.status !== TICKET_STATUS_OPTIONS.RESOLVED &&
          ticketTypeOptions?.filter(
            (item) => item.key === formData?.ticket_type
          )?.[0]?.enable_intent_section &&
          (values?.conversion_intent === 'High' ||
            values?.conversion_intent === 'Medium') ? (
            <FormControl
              variant="outlined"
              {...(!isEmpty(errors.plan) ? { error: true } : null)}
              required
            >
              <AutoComplete
                name="interested_programs"
                label="Program Interested In"
                multiple={true}
                mappedOptions={interestedProgramsOptions.map((s) => ({
                  name: s.name,
                  value: s.uuid
                }))}
                onChange={(name, value) => {
                  setFieldValue('interested_programs', value ? value : null)
                }}
                defaultValue={formData?.interested_programs}
                value={values?.interested_programs}
                placeholder="Program Interested In"
                clearOnBlur
              />
              {!isEmpty(errors.plan) && (
                <FormHelperText>{errors.plan}</FormHelperText>
              )}
            </FormControl>
          ) : null}
          {values?.status !== TICKET_STATUS_OPTIONS.RESOLVED &&
          ticketTypeOptions?.filter(
            (item) => item.key === formData?.ticket_type
          )?.[0]?.enable_intent_section &&
          (values?.conversion_intent === 'High' ||
            values?.conversion_intent === 'Medium') ? (
            <FormControl variant="outlined" required>
              <AutoComplete
                name="tentative_joining_date"
                label="Tentative Joining Month"
                multiple={false}
                mappedOptions={monthOptions?.map((s) => {
                  return {
                    value: s?.uuid,
                    name: dayjs(s?.uuid, ['YYYY-MM-DD', 'DD-MM-YYYY']).format(
                      'MMM YYYY'
                    )
                  }
                })}
                onChange={(name, value) => {
                  setFieldValue('tentative_joining_date', value ? value : null)
                }}
                defaultValue={formData?.tentative_joining_date}
                value={values?.tentative_joining_date}
                placeholder="Tentative Joining Month"
                clearOnBlur
              />
            </FormControl>
          ) : null}
          {values?.status !== TICKET_STATUS_OPTIONS.RESOLVED &&
          ticketTypeOptions?.filter(
            (item) => item.key === formData?.ticket_type
          )?.[0]?.enable_intent_section ? (
            <FormControl variant="outlined" required>
              <TextField
                name={'intent_reason'}
                label={'What reasons indicate the selected intent?'}
                variant="outlined"
                required
                validate={validateNotEmpty({ isRequired: true })}
                maxLength={800}
              />
            </FormControl>
          ) : null}
        </>
      ) : null}
      {values?.status === TICKET_STATUS_OPTIONS.RESOLVED &&
      ticketTypeOptions?.filter(
        (item) => item.key === formData?.ticket_type
      )?.[0]?.requires_conclusion ? (
        <FormControl variant="outlined" required>
          <InputLabel>{'Resolution Conclusion'}</InputLabel>
          <Select
            name={'resolution_conclusion'}
            defaultValue={formData?.resolution_conclusion || ''}
            label={'Resolution Conclusion'}
            validate={validateNotEmpty({ isRequired: true })}
          >
            {resolutionConclusionOptions?.map((key, index) => {
              return (
                <MenuItem
                  key={resolutionConclusionOptions[index]}
                  value={resolutionConclusionOptions[index]}
                >
                  {resolutionConclusionOptions[index]}
                </MenuItem>
              )
            })}
          </Select>
          {!isEmpty(errors.resolution_conclusion) && (
            <FormHelperText>{errors.resolution_conclusion}</FormHelperText>
          )}
        </FormControl>
      ) : null}
      {values?.status === TICKET_STATUS_OPTIONS.RESOLVED ? (
        <FormControl
          variant="outlined"
          {...(!isEmpty(errors.resolution_summary) ? { error: true } : null)}
          required
        >
          <TextField
            name={'resolution_summary'}
            label={'Resolution Summary'}
            variant="outlined"
            required
            validate={validateResolutionSummary}
          />
        </FormControl>
      ) : null}
      {formData?.status !== TICKET_STATUS_OPTIONS.RESOLVED &&
      formData.follow_up_date !== dayjs().format('YYYY-MM-DD') &&
      dayjs(formData.follow_up_date).isAfter(dayjs()) ? (
        <styles.Message>{FOLLOW_UP_DATE_MESSAGE}</styles.Message>
      ) : null}
    </>
  )
}

export default EditTicketStatusForm
